import { useRef, useState } from "react";
import React from "react";
import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";
import Button from "@mui/material/Button";
import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import TextField from "@mui/material/TextField";
import Snackbar, { SnackbarOrigin } from "@mui/material/Snackbar";
import { withStyles, makeStyles } from "@mui/styles";
import { useEffect } from "react";
// GCP IMPORTS
import GCPUploadService from "utils/gcp/upload";
const sanitize = require("sanitize-filename");

const useStyles = makeStyles({
  helpertext: {
    "& .css-pjy7bd-MuiFormHelperText-root": {
      marginLeft: 0,
    },
  },
});

type UploadProps = {
  onChange: any;
  fields: any;
  getValues: any;
  classes: any;
  value: any;
  className: string;
  watch?: any;
  errors: any;
  indexNumber?: any;
};

export interface State extends SnackbarOrigin {
  open: boolean;
  message: string;
}

const styles = {
  snackbarStyleViaContentProps: {
    backgroundColor: "red",
    color: "white",
  },
  snackbarStyleViaNestedContent: {
    backgroundColor: "green",
    color: "white",
  },
};

const Upload = ({
  onChange,
  fields,
  getValues,
  classes,
  className,
  value,
  watch,
  errors,
  indexNumber,
}: UploadProps) => {
  const styles = useStyles();
  const [showLoading, setShowLoading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [images, setImages] = useState<any>([]);
  const [imagesNamesList, setImagesNamesList] = useState<string[]>([]);

  const [state, setState] = React.useState<any>({
    open: false,
    vertical: "top",
    horizontal: "right",
    message: "",
  });
  const { vertical, horizontal, open } = state;

  // ---------------------- VALIDATION HELPERS ----------------------
  const isReqFieldsNotEmpty = (obj: {}) => {
    if (obj) {
      return Object.keys(obj).every((key) => getValues(key) !== "");
    }
    return false;
  };

  const handleClose = (event: any, reason?: any) => {
    if (reason === "clickaway") {
      return;
    }
    setState((prev) => ({ ...prev, open: false, message: "" }));
  };

  const requiredFieldsToEnable = (obj: {}) => {
    let fieldNames: string[] = [];
    if (obj) {
      Object.keys(obj).forEach((key) => fieldNames.push(key));
      return (
        <span style={{ fontSize: "12px" }}>
          {fieldNames.join(",")} is required to enable upload
        </span>
      );
    }
    return "";
  };

  // ---------------------- GCP PATH GENERATION ----------------------
  const getPath = () => {
    const values = getValues();
    let path = "others";

    if (fields.feature === "assignments") {
      path = `assignments/${values.class_id}/course/${values.subject_id}/chapter`;
    } else if (fields.feature === "schedule") {
      path = `schedule/${values.class_id}/${values.course_id}/`;
    } else if (fields.feature === "term-exams") {
      path = `descriptive-exam/${values.class_id}`;
    } else if (fields.feature === "learn") {
      path = `learn/resources/${values.class_id}/${
        values.course_id || "course"
      }/${values.subject_id || "subject"}/${
        values.topic_id || values.chapter_id || "chapter"
      }`;
    } else if (fields.feature === "build-test") {
      path = `build-test`;
    } else if (fields.feature === "announcements") {
      path = `announcements`;
    } else if (fields.feature === "fun-sheet") {
      path = `fun-sheet`;
    } else if (fields.feature === "miscellaneous") {
      path = `instructions`;
    }
    return path;
  };

  // Map component feature names to GCP feature keys
  const getGCPFeature = ()=> {
    const mapping = {
      assignment: "assignment",
      assignments: "assignments-ncms",
      "term-exams": "term-exams-ncms",
      learn: "learn-ncms",
      schedule: "monthly-schedule-ncms",
      "monthly-schedule": "monthly-schedule-ncms",
      "fun-sheet": "miscellaneous-ncms",
      miscellaneous: "miscellaneous-ncms",
      announcements: "announcement-ncms",
      "build-test": "miscellaneous-ncms",
    };
    return mapping[fields.feature] || "assignments-ncms";
  };

  // ---------------------- GCP MULTI-FILE UPLOAD ----------------------
  const handleCapture = async (
    event: React.ChangeEvent<HTMLInputElement> | any
  ) => {
    let files = event.target.files;
    if (!files || files.length === 0) return;

    // Generate unique filenames for all files
    let nxtFileNames: any = Object.values(files).map((item: any, idx) => {
      if (fields.isSameFileName) {
        return sanitize(item.name);
      }
      return `${Date.now() + idx}${item.name.slice(
        item.name.lastIndexOf(".")
      )}`;
    });

    // Validation
    let fileSize = fields.size || 50;
    const sizes = Object.values(files).map((item: any) => item.size);
    const totalSize = sizes.reduce((pre, curr) => pre + curr, 0);
    let isFileSizeOverflow = !(Math.ceil(totalSize / 1000000) <= fileSize);

    // Check first file extension (or validate all if needed)
    let extensionType = files[0].name.substring(
      files[0].name.lastIndexOf(".") + 1
    );

    if (isFileSizeOverflow || !fields.fileType.includes(`.${extensionType}`)) {
      let message = isFileSizeOverflow
        ? `File size is more than ${fileSize}MB, Please select valid file`
        : "Please select valid file type";
      setState((prev) => ({
        ...prev,
        open: true,
        message: message,
      }));
      event.target.value = null;
      return;
    }

    // ---------------- UPLOAD START ----------------
    try {
      setShowLoading(true);

      const gcpFeature = getGCPFeature();
    
      const dynamicPath = getPath();

      let finalResult: any = [];
      let fileUrls: string[] = [];

      // Upload all files
      for (let i = 0; i < nxtFileNames.length; i++) {
        const file = files[i];
        const fileName = nxtFileNames[i];

        const fullFilePath = [ dynamicPath, fileName]
          .filter(Boolean)
          .join("/");

        const uploadResult = await GCPUploadService.generateSignedUrlAndUpload(
          gcpFeature,
          fullFilePath,
          file,
          file.type
        );

        if (uploadResult.success && uploadResult.signedUrlResponse) {
          const publicUrl =
            uploadResult.signedUrlResponse.file_url ||
            uploadResult.signedUrlResponse.signed_url.split("?")[0];

          finalResult.push(uploadResult);
          fileUrls.push(publicUrl);
        } else {
          throw new Error(
            uploadResult.error || `Upload failed for file ${i + 1}`
          );
        }
      }

      // ---------- SUCCESS UI ----------
      if (finalResult.length > 0) {
        setShowLoading(false);
        setState((prev) => ({
          ...prev,
          open: true,
          message: "Successfully Uploaded",
        }));

        setImagesNamesList([...imagesNamesList, ...nxtFileNames]);
        setImages([...value, ...fileUrls]);

        // Clear input
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
      }
      event.target.value = null;
    } catch (err: any) {
      setShowLoading(false);
      console.log(`Upload error: ${err}`);
      setState((prev) => ({
        ...prev,
        open: true,
        message: err?.message || "Upload failed",
      }));
    }
  };

  useEffect(() => {
    onChange(images);
  }, [images]);

  return (
    <>
      {showLoading && (
        <Backdrop sx={{ color: "#fff", zIndex: 1301 }} open={showLoading}>
          <CircularProgress color="inherit" />
        </Backdrop>
      )}
      <div className={`file-styles ${className}`}>
        <label htmlFor="contained-button-file" className="file-styles">
          <input
            type="file"
            name={fields.id}
            onChange={handleCapture}
            disabled={fields.fields && !isReqFieldsNotEmpty(fields.fields)}
            style={{ display: "none" }}
            accept={fields.acceptTypes}
            ref={fileInputRef}
            multiple={true}
          />
          <div
            style={{
              display: "flex",
              justifyContent: "flex-start",
              alignItems: "center",
            }}
          >
            <TextField
              size="small"
              variant="outlined"
              name={fields.id}
              label={fields.label}
              disabled
              InputLabelProps={{ shrink: true }}
              helperText={`(max file size: ${fields.size || 50}MB)`}
              value="Upload Image"
              sx={{ width: "90%" }}
              className={styles.helpertext}
            />
            <Button
              startIcon={<CloudDownloadIcon />}
              aria-label="upload pdf"
              component="span"
              onClick={() =>
                fileInputRef.current && fileInputRef.current.click()
              }
              sx={{
                color: `${
                  getValues(fields.id)
                    ? "green"
                    : isReqFieldsNotEmpty(fields.fields)
                    ? "black"
                    : "#3f51b5"
                }`,
                marginBottom: "25px",
                marginLeft: "10px",
              }}
              disabled={fields.fields && !isReqFieldsNotEmpty(fields.fields)}
            >
              BROWSE
            </Button>
          </div>
        </label>
        {!isReqFieldsNotEmpty(fields.fields) && (
          <p style={{ color: "red", textAlign: "start" }}>
            {requiredFieldsToEnable(fields.fields)}
          </p>
        )}
        <Snackbar
          open={open}
          autoHideDuration={3000}
          onClose={handleClose}
          message={state.message}
          anchorOrigin={{ vertical, horizontal }}
          ContentProps={{
            "aria-describedby": "message-id",
            className: state.message.includes("Successfully Uploaded")
              ? classes.snackbarStyleViaNestedContent
              : classes.snackbarStyleViaContentProps,
          }}
        />
      </div>
    </>
  );
};

export default withStyles(styles)(Upload);